import DSBox from '../../../../../design-system/DSBox';
import DSGrid from '../../../../../design-system/DSGrid';
import DSKlydoCard from '../../../../../design-system/Klydo/DSKlydoCard';
import DSDraggableCard from '../../../../../design-system/DSDraggableCard';
import AddIcon from '@mui/icons-material/Add';
import { useTheme, SxProps, Theme } from '@mui/material';
import DSValueTypography from '../../../../../design-system/Typography/DSValueTypography';
import KlydoCard from '../../../../Klydos/KlydosList/components/KlydoCard';
import { PopulatedKlydo } from '../../../../../api/klydo';
import { useDragAndDrop } from '../../../../../hooks/useDragAndDrop';
import { PendulumLogoType } from '../../../../../consts/pendulumLogo';

type CollectionContentTabProps = {
  handleOpenPickModal: () => void;
  collectionKlydos: PopulatedKlydo[];
  collectionLogo: string;
  collectionPendulumLogoSize?: number;
  // Approval bakes pendulumLogoSize into logoUrl once, at approval - once
  // true, the preview must stop applying any client-side size scaling since
  // logoUrl already arrives pre-sized (see Pendulum.tsx).
  isApprovedCollection?: boolean;
  // See resolveLogoDiameterPx: 'full' fills the whole ball regardless of
  // pendulumLogoSize, matching klydo-api's cover-crop bake for that type.
  collectionPendulumLogoType?: PendulumLogoType;
  handleRemoveKlydo: (klydoId: string) => void;
  handleKlydoCardClick: (klydo: PopulatedKlydo) => void;
  onOrderChange?: (klydos: PopulatedKlydo[]) => void;
};

const CollectionContentTab = ({
  handleOpenPickModal,
  collectionKlydos,
  collectionLogo,
  collectionPendulumLogoSize,
  isApprovedCollection,
  collectionPendulumLogoType,
  handleRemoveKlydo,
  handleKlydoCardClick,
  onOrderChange,
}: CollectionContentTabProps) => {
  const { spacing, palette } = useTheme();

  const {
    displayItems: displayKlydos,
    draggedIndex,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    draggedItem,
  } = useDragAndDrop({
    items: collectionKlydos,
    onOrderChange,
  });

  const AddKlydoCard = ({ sx }: { sx?: SxProps<Theme> }) => {
    return (
      <DSKlydoCard
        onClick={handleOpenPickModal}
        sx={{
          cursor: 'pointer',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: spacing(2),
          minWidth: spacing(42),
          minHeight: spacing(30),
          ...sx,
        }}
      >
        <AddIcon
          sx={{
            backgroundColor: palette.action.disabledBackground,
            height: spacing(8),
            width: spacing(8),
            padding: spacing(2),
            borderRadius: 50,
            color: palette.secondary.contrastText,
          }}
        />
        <DSValueTypography fontSize={spacing(2.5)}>Add Klydo</DSValueTypography>
      </DSKlydoCard>
    );
  };

  if (!collectionKlydos || collectionKlydos.length === 0) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          width: '100%',
          padding: spacing(4),
          flex: 1,
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            borderRadius: spacing(3),
            border: `2px dashed ${palette.white[12]}`,
            background: 'transparent',
          }}
        >
          <AddKlydoCard sx={{ marginY: spacing(4) }} />
        </DSBox>
      </DSBox>
    );
  }

  return (
    <DSBox
      sx={{
        display: 'flex',
        paddingBottom: 10,
        width: '100%',
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          padding: spacing(6),
          borderRadius: spacing(3),
          border: `2px dashed ${palette.white[12]}`,
          background: 'transparent',
        }}
      >
        <DSGrid
          container
          spacing={{
            xs: spacing(3),
            sm: spacing(5),
            md: spacing(6),
          }}
          sx={{
            justifyContent: 'center',
            maxWidth: '100%',
            '& > *': {
              transition: 'transform 0.25s cubic-bezier(0.2, 0, 0, 1)',
            },
          }}
        >
          <AddKlydoCard />
          {displayKlydos.map((klydo: PopulatedKlydo, index: number) => (
            <DSDraggableCard
              key={klydo.id}
              originalIndex={collectionKlydos.findIndex(
                (collectionKlydo) => collectionKlydo.id === klydo.id,
              )}
              displayIndex={index}
              isDragging={draggedItem(klydo)}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              handleDragOver={handleDragOver}
              handleDragEnter={handleDragEnter}
            >
              <KlydoCard
                klydo={klydo}
                isStatic
                isDraggedHovered={draggedItem(klydo)}
                isDragging={draggedIndex !== null}
                showDragIconOnHover
                handleRemoveKlydoFromCollection={handleRemoveKlydo}
                pendulumLogo={collectionLogo}
                pendulumLogoSize={collectionPendulumLogoSize}
                isApprovedCollection={isApprovedCollection}
                pendulumLogoType={collectionPendulumLogoType}
                onSelect={handleKlydoCardClick}
                cardNumber={index + 1}
              />
            </DSDraggableCard>
          ))}
        </DSGrid>
      </DSBox>
    </DSBox>
  );
};

export default CollectionContentTab;
